<template>
  <v-card flat>
    <v-card-text>
      <!-- Responsible / Uitvoerder -->
      <v-autocomplete
        :label="$t('responsible')"

        :loading="isLoading"
        cache-items
        clearable
        v-model="responsibleId"
        :items="responsibleItems"
        :search-input.sync="responsibleSearch"
        item-value="id"
        item-title="name"
        @update:model-value="$nextTick(() => $emit('change', { employeeId: $event }))"
        variant="underlined"
      ></v-autocomplete>

      <!-- Client contacts -->
      <v-autocomplete
        :label="$t('clientResponsible')"

        :loading="isLoading || isLoadingClientContacts"
        clearable
        v-model="clientResponsibleId"
        :items="clientResponsibleItems"
        :search-input.sync="clientResponsibleSearch"
        item-value="id"
        item-title="name"
        v-if="job.client_id"
        variant="underlined"
      >
        <template v-slot:prepend>
          <v-icon
            v-tooltip="$t('add')"
            @click="$emit('show-contact-dialog')">mdi-plus</v-icon>
          <v-icon
            v-if="clientResponsibleId"
            icon="mdi-pencil"
            v-tooltip="$t('edit')"
            class="ml-2"
            @click="$emit('show-contact-dialog', clientResponsibleId)"
          />
          <v-icon v-else icon="mdi:void" class="ml-2" />
        </template>
      </v-autocomplete>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { fetchClientContacts } from '@/api/clients'
import store from '@/store';
import emitArray from '@/utils/emit';
import { ref, onMounted, computed, nextTick, watch, onBeforeUnmount } from 'vue';
import { cloneDeep } from 'lodash-es';

import { useEventBus } from '@vueuse/core'
const createdBus = useEventBus('created-employees');
const updateBus = useEventBus('updated-employees');

const emit = defineEmits(emitArray.value)
const props = defineProps({
  job: {
    type: Object,
    required: true
  }
})

const responsibleId = ref(null)
const responsibleSearch = ref('')
const clientResponsibleId = ref(null)
const clientResponsibleItems = ref([] as any[])
const clientResponsibleSearch = ref('')
const isLoading = ref(true)
const isLoadingClientContacts = ref(true)
const originalJob = ref(null as any)

const employees = computed(() => store.getters['cache.employees'])

const responsibleItems = computed(() => {
  return employees.value.filter((employee: any) => employee.responsible === 1)
})

watch(
  () => props.job.client_id,
  () => {
    fetchClientContactsLocal()
  }
)

createdBus.on(({ type, data }: any) => {
  if (type === 'executor') {
    clientResponsibleItems.value.push(data)
    clientResponsibleId.value = data.id
  }
})
updateBus.on(({ type, data }: any) => {
  if (type === 'executor') {
    clientResponsibleItems.value.forEach(obj => {
      if (obj.id == data.id) {
        obj.name = data.name
      }
    });
    clientResponsibleId.value = null
    nextTick(() => {
      clientResponsibleId.value = data.id
    })
  }
})

onBeforeUnmount(() => {
  createdBus.reset()
  updateBus.reset()
})

onMounted (async() => {
  fetchClientContactsLocal()
  originalJob.value = cloneDeep(props.job)
  initializeInputs()
  isLoading.value = false

})

const fetchClientContactsLocal = async() => {
  if (props.job.client_id) {
    isLoadingClientContacts.value = true
    const response = await fetchClientContacts({ clientId: props.job.client_id })
    clientResponsibleItems.value = response.data || []
    isLoadingClientContacts.value = false
  } else {
    clientResponsibleItems.value = []
  }
}
const setResponsibleId = (contactId: any) => {
  clientResponsibleId.value = contactId
}
const initializeInputs = () => {
  nextTick(() => {
    responsibleId.value = originalJob.value.responsible ? originalJob.value.responsible.id : null
    clientResponsibleId.value = originalJob.value.client_responsible_1 ? originalJob.value.client_responsible_1.id : null
  })
}

watch(
  () => clientResponsibleId.value,
  () => emit('change', { clientResponsibleId: clientResponsibleId.value })
)

defineExpose({
  initializeInputs,
  setResponsibleId,
  fetchClientContacts
});
</script>
